import {
  ArrowDownRight,
  ArrowUpRight,
  BadgeCheck,
  Lock,
  Minus,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Bar,
  BarChart,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  getKpiDashboard,
  readDashboardSnapshot,
  writeDashboardSnapshot,
  type KpiDashboard,
  type KpiDashboardDelta,
  type KpiDashboardMetrics,
} from '@/services/googleAdsKpiDashboardService';
import { useAdsViewStore } from '@/stores/useAdsViewStore';
import {
  DEFAULT_TREND_METRIC_KEY,
  KpiTrendChart,
  TREND_METRIC_KEYS,
} from './KpiTrendChart';
import {
  clamp01,
  formatMetric,
  formatPercentDelta,
  pct,
  type MetricFormat,
} from './kpiFormat';

interface KpiDashboardTabProps {
  tenantId: string;
}

// Metric keys whose direction is "lower is better" (a decrease is good).
const LOWER_IS_BETTER = new Set([
  'cost_per_conversion_usd',
  'cost_per_lead_usd',
  'cpc_usd',
  'lost_impression_share_budget',
  'lost_impression_share_rank',
]);

/**
 * Metrics with no good direction at all.
 *
 * Spend was in neither set, so it fell to the "higher is better" default and
 * a cost increase was reported as "the direction you want" in green. Spending
 * more can be entirely intended or entirely wrong — the dashboard cannot know
 * which, so it states the movement and passes no verdict. Raised by Art,
 * 2026-09-22: "the spend row also calls a cost increase 'the direction you
 * want'".
 */
const DIRECTIONLESS = new Set(['spend_usd']);

interface TileSpec {
  key: string;
  label: string;
  format: MetricFormat;
}

const TIER1_TILES: TileSpec[] = [
  { key: 'spend_usd', label: 'Spend', format: 'money' },
  { key: 'conversions', label: 'Conversions', format: 'decimal' },
  // Leads and CPL sit beside conversions on purpose — the pair invites the
  // comparison. Cost / conversion counts every tracked action; cost / lead
  // counts only someone who made contact, so a wide gap between the two is a
  // conversion-tracking problem worth noticing.
  { key: 'leads', label: 'Leads', format: 'decimal' },
  { key: 'cost_per_lead_usd', label: 'Cost / lead', format: 'money' },
  { key: 'cost_per_conversion_usd', label: 'Cost / conversion', format: 'money' },
  // Budget and pacing, from Google Ads. Art, 2026-07-22: the client view shows
  // the Google Ads budget and pacing derived from Google spend/budget, because
  // a client-facing account is self-managed and the retainer is not ours to
  // show. The Planner retainer pacing is internal-only and separate work.
  //
  // Budget deliberately carries no period-over-period delta: Google exposes no
  // budget history, so the previous period comes back null rather than
  // repeating today's number and implying it never changed.
  { key: 'daily_budget_usd', label: 'Daily budget', format: 'money' },
  { key: 'budget_utilisation', label: 'Budget used', format: 'percent' },
  { key: 'clicks', label: 'Clicks', format: 'number' },
  { key: 'ctr', label: 'CTR', format: 'percent' },
  { key: 'cpc_usd', label: 'CPC', format: 'money' },
  { key: 'impressions', label: 'Impressions', format: 'number' },
  { key: 'conversion_rate', label: 'Conversion rate', format: 'percent' },
];

const TIER2_TILES: TileSpec[] = [
  { key: 'impression_share', label: 'Impression share', format: 'percent' },
  {
    key: 'lost_impression_share_budget',
    label: 'Lost IS — budget',
    format: 'percent',
  },
  {
    key: 'lost_impression_share_rank',
    label: 'Lost IS — rank',
    format: 'percent',
  },
];

export function KpiDashboardTab({ tenantId }: KpiDashboardTabProps) {
  // The metric the Daily trend chart shows. The ONE copy: comparison rows
  // set it, the chart's own pills set it, and the row highlight reads it, so
  // the highlight always matches the chart (Spend on first load included).
  const [chartedMetricKey, setChartedMetricKey] = useState<string>(
    DEFAULT_TREND_METRIC_KEY,
  );
  // Chart or table view of the Daily trend. Lifted for the same reason: a
  // row click has to be able to switch the table back to the chart.
  const [showTrendTable, setShowTrendTable] = useState(false);
  const trendRef = useRef<HTMLDivElement>(null);
  // A row click always sets the chart, even when that metric is already
  // charted, shows the chart (not the table), and brings it into view if it
  // is off screen.
  const chartFromRow = useCallback((key: string) => {
    if (!TREND_METRIC_KEYS.has(key)) return;
    setChartedMetricKey(key);
    setShowTrendTable(false);
    revealIfHidden(trendRef.current);
  }, []);
  // The internal/client lens and the tier control now live in the shared page
  // header (AdsViewControls). This tab is purely the metrics + charts view; the
  // header's tier PATCH invalidates ['kpi-dashboard', tenantId], so changing
  // tier there auto-refreshes this dashboard.
  //
  // Tier policy per lens (Carl, 2026-07-23):
  //   - INTERNAL view is the tier-3 equivalent — operators see EVERYTHING,
  //     regardless of what the tenant bought. The tier model exists to gate
  //     what a CLIENT sees, not to blindfold the agency working the account.
  //   - CLIENT view shows the tenant's real tier, unless the operator sets an
  //     ephemeral "preview as tier" override to see what a given package
  //     looks like.
  // preview_tier=3 rides the existing operator-only override path server-side,
  // so no backend change and no way for a real client session to reach it
  // (non-operators sending preview_tier are 403'd at the route).
  const viewMode = useAdsViewStore((s) => s.viewMode);
  const previewTier = useAdsViewStore((s) =>
    s.viewMode === 'client' ? s.previewTier : 3,
  );
  // Snapshot policy: internal(=tier 3) and client-real-tier are both STANDARD
  // views an operator returns to daily, so each keeps stale-while-revalidate
  // under its own key (different payload shapes must never cross lenses). An
  // ephemeral client "preview as tier" stays snapshot-less as before.
  const isClientPreview = viewMode === 'client' && previewTier != null;
  const snapshotKey =
    viewMode === 'internal' ? `${tenantId}::internal` : tenantId;

  const dashboard = useQuery({
    // Preview tier participates in the key so switching preview refetches with
    // its own gating and never collides with the real-tier cache entry.
    queryKey: ['kpi-dashboard', tenantId, previewTier],
    queryFn: () => getKpiDashboard(tenantId, previewTier ?? undefined),
    // The cold fetch is slow (live Google Ads); don't retry 3× at 90s each,
    // and treat the result as fresh for the backend cache window so mounts /
    // window-focus don't re-trigger a live fetch.
    retry: 1,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // Stale-while-revalidate: paint the last saved snapshot instantly on
    // mount while a fresh fetch still runs in the background. While the snapshot
    // is showing, `dashboard.isPlaceholderData` is true. NEVER seed from (or
    // save) a snapshot for a transient preview — only the real-tier view.
    placeholderData: () =>
      isClientPreview ? undefined : readDashboardSnapshot(snapshotKey),
  });

  // Save only genuinely-fresh payloads of a STANDARD view (never an
  // ephemeral client preview, never the snapshot echoed back as placeholder
  // data) so the next mount can paint instantly.
  useEffect(() => {
    if (!isClientPreview && dashboard.data && !dashboard.isPlaceholderData) {
      writeDashboardSnapshot(snapshotKey, dashboard.data);
    }
  }, [isClientPreview, snapshotKey, dashboard.data, dashboard.isPlaceholderData]);

  // Full skeleton ONLY on a true cold start — no snapshot to show and the very
  // first fetch is still in flight.
  if (!dashboard.data && dashboard.isLoading) {
    return <DashboardSkeleton />;
  }

  // Full error card ONLY when the fetch failed with nothing at all to show.
  // If a stale snapshot is present, the dashboard stays up and the failure is
  // surfaced inline (soft-fail note) instead.
  if (dashboard.isError && !dashboard.data) {
    return (
      <Card>
        <CardContent className="flex flex-col gap-3 py-6 text-sm">
          <div className="flex items-start gap-2 text-destructive">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p>Could not load the KPI dashboard: {extractError(dashboard.error)}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                The first load pulls live metrics from Google Ads and can take a
                few seconds. Results are cached for 10 minutes afterwards.
              </p>
            </div>
          </div>
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void dashboard.refetch()}
              disabled={dashboard.isFetching}
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${dashboard.isFetching ? 'animate-spin' : ''}`}
              />
              {dashboard.isFetching ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Data present (snapshot or fresh) → render immediately.
  const data = dashboard.data as KpiDashboard;
  const noData = data.tier1.current.available === false;
  // A background refresh over a stale snapshot that failed: keep the dashboard,
  // show an inline amber note rather than swapping to the error card.
  const softFailedRefresh = dashboard.isError && !!dashboard.data;

  return (
    <div className="space-y-6">
      <DashboardHeader data={data} previewActive={previewTier != null} />

      <RefreshStatus
        isFetching={dashboard.isFetching}
        isPlaceholderData={dashboard.isPlaceholderData}
        softFailedRefresh={softFailedRefresh}
        onRetry={() => void dashboard.refetch()}
      />

      {noData ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            No spend in this period. Metrics will populate once the account
            starts serving.
          </CardContent>
        </Card>
      ) : null}

      <div className="relative space-y-6">
        {/* Tier 1 — Performance (always shown) */}
        <Card>
          <CardHeader>
            <CardTitle>Performance</CardTitle>
            <CardDescription>
              Core account performance for the current period, compared to the
              previous one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <PeriodComparison
              current={data.tier1.current}
              previous={data.tier1.previous}
              deltas={data.tier1.deltas}
              chartedMetricKey={chartedMetricKey}
              onChartMetric={chartFromRow}
            />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {TIER1_TILES.map((tile) => (
                <MetricTile
                  key={tile.key}
                  spec={tile}
                  current={
                    data.tier1.current[tile.key as keyof KpiDashboardMetrics]
                  }
                  delta={data.tier1.deltas?.[tile.key]}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Daily trend (GH #933). Its own query and its own error/empty
            handling, so a failing series never takes the tiles down with it.
            Fed the dashboard's window so the two always describe the same
            days. No tier gate — every point is Tier-1 data. */}
        <div ref={trendRef} className="scroll-mt-4">
          <KpiTrendChart
            tenantId={tenantId}
            periodStart={data.period.current_start}
            periodEnd={data.period.current_end}
            metricKey={chartedMetricKey}
            onMetricChange={setChartedMetricKey}
            showTable={showTrendTable}
            onShowTableChange={setShowTrendTable}
          />
        </div>

        {/* Tier 2 — Competitive & efficiency */}
        <Card>
          <CardHeader>
            <CardTitle>Competitive &amp; efficiency</CardTitle>
            <CardDescription>
              Impression share and wasted spend — available on the managed tier.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {data.tier2.unlocked && data.tier2.current ? (
              <>
                {data.tier2.current.impression_share != null ? (
                  <ImpressionShareChart metrics={data.tier2.current} />
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {TIER2_TILES.map((tile) => (
                    <MetricTile
                      key={tile.key}
                      spec={tile}
                      current={
                        data.tier2.current?.[
                          tile.key as keyof KpiDashboardMetrics
                        ]
                      }
                      delta={data.tier2.deltas?.[tile.key]}
                    />
                  ))}
                </div>
              </>
            ) : (
              <LockedTier2Card />
            )}
          </CardContent>
        </Card>
      </div>

      {/* CORE-G-3: retired the "Client-safe view" framing. The one-liner
          summary text still ships; the API field (`client_safe_summary`)
          stays because it is the contract with the backend. */}
      <Card className="border-teal/30 bg-teal-light/20">
        <CardContent className="py-4">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-teal-dark">
            <BadgeCheck className="h-3.5 w-3.5" />
            Summary
          </div>
          <p className="text-sm text-muted-foreground">
            {data.client_safe_summary || 'No summary available for this period.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header — tenant + tier + period summary (lens/tier controls live in the
// shared page header, AdsViewControls).
// ---------------------------------------------------------------------------

function DashboardHeader({
  data,
  previewActive,
}: {
  data: KpiDashboard;
  previewActive: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">{data.tenant_id}</h2>
          {/* Badge reflects the tier actually used for gating (effective),
              which is the preview tier when previewing. */}
          <TierBadge tier={data.effective_tier} />
          {previewActive ? (
            <span className="inline-flex items-center rounded-full border border-amber/40 bg-amber-light px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-dark">
              Preview
            </span>
          ) : null}
        </div>
        {/* Both ranges are named. "Last 30 vs previous 30" followed by a
            single date range left it ambiguous which one was on screen. */}
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Current 30 days</span>{' '}
          {data.period.current_start}–{data.period.current_end}
          <span className="px-2 text-border">|</span>
          Previous 30 days {data.period.previous_start}–
          {data.period.previous_end}
        </p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Refresh status — a subtle "refreshing…" pill (SWR) and a soft-fail note when
// a background refresh over a stale snapshot fails. Hidden when idle & healthy.
// ---------------------------------------------------------------------------

function RefreshStatus({
  isFetching,
  isPlaceholderData,
  softFailedRefresh,
  onRetry,
}: {
  isFetching: boolean;
  isPlaceholderData: boolean;
  softFailedRefresh: boolean;
  onRetry: () => void;
}) {
  if (!isFetching && !softFailedRefresh) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {isFetching ? (
        <div className="inline-flex items-center gap-1.5 rounded-full bg-teal-light/40 px-2.5 py-1 text-xs font-medium text-teal-dark">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          {isPlaceholderData
            ? 'Showing last snapshot — refreshing…'
            : 'Refreshing…'}
        </div>
      ) : null}

      {softFailedRefresh ? (
        <div className="inline-flex items-center gap-2 text-xs text-amber-dark">
          <span className="inline-flex items-center gap-1.5">
            <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
            Couldn&apos;t refresh — showing last known values.
          </span>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1 font-medium text-amber-dark underline underline-offset-2 hover:text-amber"
          >
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TierBadge({ tier }: { tier: number }) {
  const label =
    tier >= 3
      ? 'Tier 3 · Premium'
      : tier === 2
        ? 'Tier 2 · Managed'
        : 'Tier 1 · Standard';
  const className =
    tier >= 2
      ? 'border-teal/40 bg-teal-light/40 text-teal-dark'
      : 'border-coral/40 bg-coral-light/40 text-coral-dark';
  return (
    <Badge variant="outline" className={className}>
      {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Period comparison (Tier 1) — previous vs current, per metric
// ---------------------------------------------------------------------------

interface PeriodRow {
  key: string;
  label: string;
  previousLabel: string;
  currentLabel: string;
  /** Raw values for the paired bars; null = n/a (a gap, never a zero bar). */
  previousValue: number | null;
  currentValue: number | null;
  /** Signed % change, null when there is nothing comparable to measure. */
  percent: number | null;
  deltaLabel: string | null;
  /**
   * true = moved the way you want, false = against you, null = no verdict.
   * null arises two ways: nothing comparable to judge against (percent ==
   * null), or the metric has no good direction (see DIRECTIONLESS). Inside
   * the bar branch percent is non-null, so null there always means the
   * second — render it muted, never red.
   */
  good: boolean | null;
  /** The metric has no good direction; suppresses the verdict clause. */
  directionless: boolean;
}

/**
 * Scroll the Daily trend card into view when a row click charts a metric,
 * unless the card is already fully on screen. Smooth, except for readers
 * who asked the system for reduced motion.
 */
function revealIfHidden(el: HTMLElement | null): void {
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const viewport = window.innerHeight || document.documentElement.clientHeight;
  if (rect.top >= 0 && rect.bottom <= viewport) return;
  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
}

/**
 * Previous period vs current period, one row per metric.
 *
 * Replaces a diverging bar chart of percentage deltas. Art's objection
 * (2026-07-21) was that "spend went down" told him nothing on its own — he
 * wanted the two periods side by side with both date ranges named.
 *
 * Both absolute values are printed — that was the missing piece. The mark
 * encodes the CHANGE rather than the values, for two reasons:
 *
 *   - Spend is in thousands, CTR is a fraction, conversions are single digits.
 *     One shared value axis across those is the classic mis-scale, and a
 *     second axis is worse.
 *   - Scaling each row to its own larger period looked plausible but carried
 *     almost no information: whenever the two periods are close, every bar
 *     lands at 90-100% and all rows look alike.
 *
 * Percentages are unitless, so a single diverging scale across rows is honest
 * and lets the eye rank movement at a glance. Teal/coral here mean direction
 * (good/bad per LOWER_IS_BETTER), which is their reserved meaning throughout
 * this dashboard.
 */
function PeriodComparison({
  current,
  previous,
  deltas,
  chartedMetricKey,
  onChartMetric,
}: {
  current: KpiDashboardMetrics;
  previous: KpiDashboardMetrics;
  deltas: Record<string, KpiDashboardDelta> | undefined;
  /** Metric currently charted below; highlights its row. */
  chartedMetricKey?: string | null;
  /** Row click → chart that metric in the daily trend. */
  onChartMetric?: (key: string) => void;
}) {
  const rows: PeriodRow[] = TIER1_TILES.reduce<PeriodRow[]>((acc, tile) => {
    const rawCurrent = current[tile.key as keyof KpiDashboardMetrics];
    const rawPrevious = previous[tile.key as keyof KpiDashboardMetrics];
    const currentValue = typeof rawCurrent === 'number' ? rawCurrent : null;
    const previousValue = typeof rawPrevious === 'number' ? rawPrevious : null;
    if (currentValue == null && previousValue == null) return acc;

    const percent = deltas?.[tile.key]?.percent;
    const lowerIsBetter = LOWER_IS_BETTER.has(tile.key);
    const directionless = DIRECTIONLESS.has(tile.key);

    acc.push({
      key: tile.key,
      label: tile.label,
      previousLabel: formatMetric(previousValue, tile.format),
      currentLabel: formatMetric(currentValue, tile.format),
      previousValue,
      currentValue,
      percent: percent ?? null,
      deltaLabel: percent == null ? null : formatPercentDelta(percent),
      good:
        percent == null || directionless
          ? null
          : percent === 0
            ? true
            : lowerIsBetter
              ? percent < 0
              : percent > 0,
      directionless,
    });
    return acc;
  }, []);

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
        No period-over-period comparison yet — this appears once a previous
        period is available.
      </div>
    );
  }

  // Diverging-row layout (restored 2026-07-23 per Carl, over the paired-bar
  // panels): one row per metric — previous value, a signed change bar around
  // a zero line, current value, Δ%. One shared percent scale across rows is
  // fair because percentages are unitless.
  //
  // Improvements over the original:
  //   - explicit column labels + a directional legend under the title;
  //   - the bar carries hue (green = moved the way you want, red = against,
  //     per LOWER_IS_BETTER) AND a value label at its tip;
  //   - hover shows a full-sentence tooltip;
  //   - INTERACTION: clicking a row charts that metric in the daily trend
  //     below (rows without a daily series — leads, budget — aren't
  //     clickable and say so).
  // ⚠ `row.percent` is a FRACTION (0.187 = +18.7%), so the floor has to be
  // one too: 0.01 is one percentage point. It used to be `1`, i.e. 100
  // points, so every real change was scaled against 100% and drawn a few
  // pixels wide — the same fraction/points confusion as formatPercentDelta.
  const maxAbsPercent = Math.max(
    ...rows.map((row) => Math.abs(row.percent ?? 0)),
    0.01,
  );
  return (
    <div>
      <div className="text-sm font-semibold">
        Previous 30 days vs current 30 days
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Bars show the % change between periods —{' '}
        <span className="font-medium text-[hsl(var(--status-success))]">green</span> moved the
        direction you want (for costs, down),{' '}
        <span className="font-medium text-[hsl(var(--status-error))]">red</span> against,{' '}
        <span className="font-medium text-muted-foreground">grey</span> has no good
        direction (spend). Click a row to see its daily trend below.
      </p>
      <div className="space-y-0.5">
        <div className="grid grid-cols-[minmax(88px,1.2fr)_minmax(64px,auto)_minmax(80px,2fr)_minmax(64px,auto)_minmax(52px,auto)] items-center gap-x-3 px-1 text-[11px] uppercase tracking-wide text-muted-foreground">
          <span>Metric</span>
          <span className="text-right">Previous</span>
          <span className="text-center normal-case">← worse · better →</span>
          <span className="text-right">Current</span>
          <span className="text-right">Δ%</span>
        </div>
        {rows.map((row) => {
          const chartable = TREND_METRIC_KEYS.has(row.key);
          const active = chartable && row.key === chartedMetricKey;
          const sentence =
            row.percent == null
              ? `${row.label}: ${row.currentLabel} this period (no comparable previous value).`
              : `${row.label} went from ${row.previousLabel} to ${row.currentLabel} (${row.deltaLabel})${
                  row.good == null
                    ? ''
                    : row.good
                      ? ' — the direction you want'
                      : ' — against you'
                }.${chartable ? ' Click to chart the daily trend.' : ''}`;
          return (
            <button
              key={row.key}
              type="button"
              disabled={!chartable}
              // The highlight, said out loud: the charted row is "pressed".
              aria-pressed={chartable ? active : undefined}
              onClick={() => chartable && onChartMetric?.(row.key)}
              title={sentence}
              className={[
                'grid w-full grid-cols-[minmax(88px,1.2fr)_minmax(64px,auto)_minmax(80px,2fr)_minmax(64px,auto)_minmax(52px,auto)] items-center gap-x-3 rounded-md px-1 py-1 text-left text-xs transition-colors',
                chartable
                  ? 'cursor-pointer hover:bg-warm-bg'
                  : 'cursor-default',
                active ? 'bg-teal-light/40 ring-1 ring-teal/30' : '',
              ].join(' ')}
            >
              <span className="truncate text-muted-foreground">
                {row.label}
                {!chartable && (
                  <span className="ml-1 text-[9px] uppercase tracking-wide opacity-60">
                    no daily
                  </span>
                )}
              </span>
              <span className="text-right tabular-nums text-muted-foreground">
                {row.previousLabel}
              </span>
              <span className="relative block h-4" aria-hidden>
                {/* Zero line: bars grow from the centre so the eye can rank
                    movement without reading a single number. */}
                <span
                  className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2"
                  style={{ backgroundColor: 'var(--color-border)' }}
                />
                {row.percent != null && row.percent !== 0 ? (
                  <>
                    <span
                      className="absolute top-1/2 h-2 -translate-y-1/2 rounded-sm"
                      style={{
                        width: `${(Math.abs(row.percent) / maxAbsPercent) * 46}%`,
                        ...(row.percent > 0
                          ? { left: '50%' }
                          : { right: '50%' }),
                        backgroundColor:
                          row.good == null
                            ? 'hsl(var(--muted-foreground))'
                            : row.good
                              ? 'hsl(var(--status-success))'
                              : 'hsl(var(--status-error))',
                      }}
                    />
                    {/* Value label at the bar tip, on the empty side. */}
                    <span
                      className={[
                        'absolute top-1/2 -translate-y-1/2 text-[9px] font-medium tabular-nums',
                        row.good == null
                          ? 'text-muted-foreground'
                          : row.good
                            ? 'text-[hsl(var(--status-success))]'
                            : 'text-[hsl(var(--status-error))]',
                      ].join(' ')}
                      style={
                        row.percent > 0
                          ? {
                              left: `calc(50% + ${(Math.abs(row.percent) / maxAbsPercent) * 46}% + 4px)`,
                            }
                          : {
                              right: `calc(50% + ${(Math.abs(row.percent) / maxAbsPercent) * 46}% + 4px)`,
                            }
                      }
                    >
                      {row.deltaLabel}
                    </span>
                  </>
                ) : null}
              </span>
              <span className="text-right font-semibold tabular-nums">
                {row.currentLabel}
              </span>
              <span
                className={
                  row.good == null
                    ? 'text-right tabular-nums text-muted-foreground'
                    : row.good
                      ? 'text-right tabular-nums text-[hsl(var(--status-success))]'
                      : 'text-right tabular-nums text-[hsl(var(--status-error))]'
                }
              >
                {row.deltaLabel ?? '—'}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Impression-share breakdown (Tier 2) — 100% stacked bar
// ---------------------------------------------------------------------------

function ImpressionShareChart({ metrics }: { metrics: KpiDashboardMetrics }) {
  const share = metrics.impression_share;
  if (share == null) return null;

  const lostBudget = metrics.lost_impression_share_budget;
  const lostRank = metrics.lost_impression_share_rank;

  const title = 'Impression share — share of available impressions';

  // Fallback gauge when both lost_* components are missing.
  if (lostBudget == null && lostRank == null) {
    const captured = clamp01(share);
    const gaugeData = [
      {
        name: 'share',
        captured,
        remaining: Math.max(0, 1 - captured),
        capturedLabel: `${pct(captured)} impression share captured`,
      },
    ];
    return (
      <div>
        <div className="mb-3 text-sm font-semibold">{title}</div>
        <ResponsiveContainer width="100%" height={70}>
          <BarChart
            layout="vertical"
            data={gaugeData}
            margin={{ top: 4, right: 8, bottom: 4, left: 8 }}
          >
            <XAxis type="number" domain={[0, 1]} hide />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              cursor={false}
              formatter={(value) => pct(Number(value))}
              contentStyle={{
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            <Bar
              dataKey="captured"
              name="Captured"
              stackId="s"
              fill="var(--color-teal)"
              stroke="var(--color-card)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="capturedLabel"
                position="insideLeft"
                fill="var(--color-ink)"
                fontSize={11}
              />
            </Bar>
            <Bar
              dataKey="remaining"
              name="Remaining"
              stackId="s"
              fill="var(--color-muted)"
              stroke="var(--color-card)"
              strokeWidth={2}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const captured = clamp01(share);
  const budget = clamp01(lostBudget ?? 0);
  const rank = clamp01(lostRank ?? 0);
  const remaining = Math.max(0, 1 - captured - budget - rank);
  const upper = Math.max(1, captured + budget + rank + remaining);

  // Only label segments large enough to fit the text.
  const seg = (value: number) => (value >= 0.06 ? pct(value) : '');
  const row = [
    {
      name: 'share',
      captured,
      budget,
      rank,
      remaining,
      capturedLabel: seg(captured),
      budgetLabel: seg(budget),
      rankLabel: seg(rank),
      remainingLabel: seg(remaining),
    },
  ];

  return (
    <div>
      <div className="mb-3 text-sm font-semibold">{title}</div>
      <ResponsiveContainer width="100%" height={96}>
        <BarChart
          layout="vertical"
          data={row}
          margin={{ top: 4, right: 8, bottom: 4, left: 8 }}
        >
          <XAxis type="number" domain={[0, upper]} hide />
          <YAxis type="category" dataKey="name" hide />
          <Tooltip
            cursor={false}
            formatter={(value) => pct(Number(value))}
            contentStyle={{
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              fontSize: 12,
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
            iconType="circle"
          />
          <Bar
            dataKey="captured"
            name="Captured"
            stackId="s"
            fill="var(--color-teal)"
            stroke="var(--color-card)"
            strokeWidth={2}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="capturedLabel"
              position="center"
              fill="var(--color-ink)"
              fontSize={11}
            />
          </Bar>
          <Bar
            dataKey="budget"
            name="Lost to budget"
            stackId="s"
            fill="var(--color-coral)"
            stroke="var(--color-card)"
            strokeWidth={2}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="budgetLabel"
              position="center"
              fill="var(--color-ink)"
              fontSize={11}
            />
          </Bar>
          <Bar
            dataKey="rank"
            name="Lost to rank"
            stackId="s"
            fill="var(--color-amber)"
            stroke="var(--color-card)"
            strokeWidth={2}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="rankLabel"
              position="center"
              fill="var(--color-ink)"
              fontSize={11}
            />
          </Bar>
          <Bar
            dataKey="remaining"
            name="Remaining / unknown"
            stackId="s"
            fill="var(--color-muted)"
            stroke="var(--color-card)"
            strokeWidth={2}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="remainingLabel"
              position="center"
              fill="var(--color-muted-foreground)"
              fontSize={11}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Metric tile with delta
// ---------------------------------------------------------------------------

function MetricTile({
  spec,
  current,
  delta,
}: {
  spec: TileSpec;
  current: number | null | undefined;
  delta: KpiDashboardDelta | undefined;
}) {
  return (
    // Plain card, not bg-warm-bg: that is the comparison rows' hover colour,
    // and on a tile it made a static number look clickable.
    <div data-testid="kpi-tile" className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{spec.label}</div>
      <div className="mt-1 text-lg font-semibold">
        {formatMetric(current, spec.format)}
      </div>
      <DeltaLine metricKey={spec.key} delta={delta} />
    </div>
  );
}

function DeltaLine({
  metricKey,
  delta,
}: {
  metricKey: string;
  delta: KpiDashboardDelta | undefined;
}) {
  if (!delta || delta.percent == null) {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        <span>no change data</span>
      </div>
    );
  }

  const pctValue = delta.percent;
  const rising = pctValue > 0;
  const flat = pctValue === 0;
  const lowerIsBetter = LOWER_IS_BETTER.has(metricKey);
  const directionless = DIRECTIONLESS.has(metricKey);
  // "Good" when the direction of movement favors the client; null when the
  // metric has no good direction, so the tile states the change without
  // colouring it as a win or a loss.
  const good = directionless
    ? null
    : flat
      ? true
      : lowerIsBetter
        ? !rising
        : rising;

  const Icon = flat ? Minus : rising ? ArrowUpRight : ArrowDownRight;
  const colorClass = flat || good == null
    ? 'text-muted-foreground'
    : good
      ? 'text-teal-dark'
      : 'text-destructive';

  return (
    <div className={`mt-1 flex items-center gap-1 text-xs font-medium ${colorClass}`}>
      <Icon className="h-3 w-3" />
      <span>{formatPercentDelta(pctValue)} vs prev</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locked Tier 2 card
// ---------------------------------------------------------------------------

function LockedTier2Card() {
  return (
    <div className="flex items-start gap-3 rounded-md border border-coral/30 bg-coral-light/30 p-4">
      <div className="rounded-full bg-warm-bg p-2 text-coral-dark">
        <Lock className="h-5 w-5" />
      </div>
      <div>
        <div className="text-sm font-semibold text-coral-dark">
          Tier 2 metrics
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Upgrade to the managed tier to unlock impression share, lost
          impression share, and wasted spend.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DashboardSkeleton() {
  // Simulated progress: the backend can't stream real progress, so creep
  // toward ~92% and decelerate — always moving so it never reads as "stuck",
  // never hitting 100% until the data actually arrives (the component unmounts).
  // Named `progress`, not `pct` — `pct` is now an imported formatter and a
  // local of that name would shadow it.
  const [progress, setProgress] = useState(8);
  useEffect(() => {
    const id = setInterval(() => {
      setProgress((p) => (p >= 92 ? 92 : p + Math.max(0.8, (92 - p) * 0.1)));
    }, 280);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-3 py-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin text-teal-dark" />
            Loading live Google Ads metrics… the first load fetches from Google
            Ads and can take a few seconds (cached afterwards).
          </div>
          <Progress value={progress} className="h-2" />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="py-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-md border border-border bg-muted/40"
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Formatters live in ./kpiFormat so the trend chart formats identically to the
// tiles beside it.

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? String(err)
  );
}
